import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { Lang, RejectReason, RoundState } from '@polyglot/engine';
import {
  charToTileSequences,
  countByLanguage,
  evaluateCandidate,
  finalWordNeedsConfirmation,
  isLanguageRequirementFulfilled,
  MODES,
  requirementOf,
  scoreRound,
  targetOf,
  timeLimitOf,
} from '@polyglot/engine';
import { Modal } from '../components/ui';
import { feedback } from '../lib/feedback';
import { formatClock, formatScore } from '../lib/format';
import { reducedMotion, useSettings } from '../lib/settings';
import type { RoundDriver } from './driver';

export interface OpponentInfo {
  label: string;
  isBot: boolean;
  progress: () => { words: number; finished: boolean };
  /** Live duels: false while the opponent is disconnected (their clock keeps running). */
  online?: boolean | null;
}

interface Props {
  driver: RoundDriver;
  opponent?: OpponentInfo | null;
  onFinished: (state: RoundState) => void;
}

type Message = { kind: 'ok' | 'error' | 'info'; text: string; langs?: Lang[]; key?: string } | null;

function useNow(driver: RoundDriver, active: boolean): number {
  const [now, setNow] = useState(() => driver.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(driver.now()), 100);
    return () => window.clearInterval(id);
  }, [driver, active]);
  return now;
}

function shuffled(n: number, current: number[]): number[] {
  const out = [...current];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  // Avoid returning the same visual order.
  if (n > 1 && out.every((v, i) => v === current[i])) return [...current.slice(1), current[0]!];
  return out;
}

export function GameScreen({ driver, opponent, onFinished }: Props) {
  const { t, i18n } = useTranslation();
  const settings = useSettings();
  const state = useSyncExternalStore(
    (cb) => driver.subscribe(cb),
    () => driver.getState(),
  );
  const playing = state.status === 'playing';
  const now = useNow(driver, playing);
  const rack = state.config.rack;
  const langs = state.config.languages;
  const mode = MODES[state.config.mode];
  const limit = timeLimitOf(state);
  const target = targetOf(state);
  const requirement = requirementOf(state);
  const started = now >= state.startedAt;
  const remaining = limit === null ? null : Math.max(0, limit - (now - state.startedAt));

  const [order, setOrder] = useState<number[]>(() => rack.map((_, i) => i));
  const [picked, setPicked] = useState<number[]>([]);
  const [attrib, setAttrib] = useState<'auto' | Lang>('auto');
  const [message, setMessage] = useState<Message>(null);
  const [confirm, setConfirm] = useState<{ key: string; lang: Lang | null } | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const liveRef = useRef<HTMLDivElement>(null);

  const key = picked.map((i) => rack[i]).join('');
  const counts = countByLanguage(state.words, langs);
  const score = useMemo(() => scoreRound(state), [state]);
  const fulfilled = isLanguageRequirementFulfilled(requirement, counts);

  useEffect(() => {
    if (state.status === 'finished') {
      feedback.end();
      const id = window.setTimeout(() => onFinished(state), reducedMotion() ? 200 : 900);
      return () => window.clearTimeout(id);
    }
  }, [state, onFinished]);

  const announce = useCallback((text: string) => {
    if (liveRef.current) liveRef.current.textContent = text;
  }, []);

  const reasonText = useCallback((r: RejectReason) => t(`reasons.${r}`), [t]);

  const doSubmit = useCallback(
    async (opts: { lang?: Lang | null; confirmFinal?: boolean; wordKey?: string } = {}) => {
      const k = opts.wordKey ?? key;
      if (!playing || !started || busy || k.length === 0) return;
      const lang = opts.lang !== undefined ? opts.lang : attrib === 'auto' ? null : attrib;
      setBusy(true);
      // Free the tiles as soon as the word is sent, so the next word can be typed during the
      // network round trip (online modes); letters typed meanwhile are never discarded.
      const sent = opts.wordKey === undefined ? picked : null;
      if (sent) setPicked([]);
      // Outcomes that used to keep the word on screen get it back, unless a new one was started.
      const restore = () => {
        if (sent) setPicked((cur) => (cur.length === 0 ? sent : cur));
      };
      // Re-submission of a kept word (language choice, confirmation): clear it once settled.
      const settle = () => {
        if (!sent) setPicked((cur) => (cur.map((i) => rack[i]).join('') === k ? [] : cur));
      };
      try {
        const outcome = await driver.submit({
          key: k,
          lang,
          confirmFinal: opts.confirmFinal ?? false,
        });
        if (outcome.ok) {
          feedback.accepted();
          settle();
          const w = outcome.word;
          const txt = t('game.accepted', {
            word: w.display,
            points: w.points,
            lang: t(`langNames.${w.lang}`),
          });
          setMessage({ kind: 'ok', text: txt });
          announce(txt);
        } else if (outcome.reason === 'confirmation_required') {
          restore();
          setConfirm({ key: k, lang });
        } else if (
          outcome.reason === 'wrong_language' &&
          outcome.validLangs &&
          outcome.validLangs.length > 0
        ) {
          feedback.rejected();
          restore();
          const txt = t('reasons.wrong_language', {
            langs: outcome.validLangs.map((l) => l.toUpperCase()).join(', '),
          });
          setMessage({ kind: 'info', text: txt, langs: outcome.validLangs, key: k });
          announce(txt);
        } else {
          feedback.rejected();
          if (!reducedMotion()) {
            setShake(true);
            window.setTimeout(() => setShake(false), 350);
          }
          const txt = reasonText(outcome.reason);
          setMessage({ kind: 'error', text: txt });
          announce(txt);
          if (outcome.reason === 'round_over') restore();
          else settle();
        }
      } catch {
        restore();
        setMessage({ kind: 'error', text: t('errors.network') });
      } finally {
        setBusy(false);
      }
    },
    [key, picked, rack, playing, started, busy, attrib, driver, t, announce, reasonText],
  );

  const pickTile = useCallback(
    (rackIndex: number) => {
      if (!playing || !started) return;
      setPicked((p) => (p.includes(rackIndex) ? p : [...p, rackIndex]));
      setMessage(null);
      feedback.tap();
    },
    [playing, started],
  );

  const backspace = useCallback(() => setPicked((p) => p.slice(0, -1)), []);
  const clear = useCallback(() => setPicked([]), []);
  const shuffle = useCallback(() => setOrder((o) => shuffled(o.length, o)), []);

  // Physical keyboard: letters, digits 1–9 (tile position), Backspace, Escape, Enter, Space.
  useEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => {
      if (confirm || confirmStop || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA')
      )
        return;
      if (e.key === 'Enter') {
        e.preventDefault();
        void doSubmit();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        backspace();
      } else if (e.key === 'Escape') {
        clear();
      } else if (e.key === ' ') {
        e.preventDefault();
        shuffle();
      } else if (/^[1-9]$/.test(e.key)) {
        const pos = Number(e.key) - 1;
        if (pos < order.length) pickTile(order[pos]!);
      } else if (e.key.length === 1) {
        // A typed character may need several tiles (ß → S S, œ → O E): pick all or none.
        for (const seq of charToTileSequences(e.key)) {
          const taken = new Set(picked);
          const idxs: number[] = [];
          for (const tile of seq) {
            const idx = order.find((i) => rack[i] === tile && !taken.has(i));
            if (idx === undefined) break;
            taken.add(idx);
            idxs.push(idx);
          }
          if (idxs.length === seq.length) {
            for (const idx of idxs) pickTile(idx);
            break;
          }
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    playing,
    confirm,
    confirmStop,
    doSubmit,
    backspace,
    clear,
    shuffle,
    pickTile,
    order,
    rack,
    picked,
  ]);

  // Live hints: Relax mode only (never in timed/competitive rounds).
  const hint = useMemo(() => {
    if (!driver.hintsAllowed || !settings.relaxHints || key.length < 3) return null;
    return evaluateCandidate(state, key, driver.lexicons);
  }, [driver, settings.relaxHints, key, state]);

  const warnFinal =
    target !== null &&
    playing &&
    state.words.length === target - 1 &&
    requirement.quotaPerLanguage > 0 &&
    langs.some((l) => finalWordNeedsConfirmation(state, l));

  const opp = opponent?.progress();
  const locale = i18n.language;
  const lowTime = remaining !== null && remaining <= 10_000;

  return (
    <div className="game" data-status={state.status}>
      <div className="sr-only" aria-live="polite" ref={liveRef} />
      <header className="hud">
        <div className={`hud-timer${lowTime ? ' low' : ''}`} aria-label={t('game.timeLeft')}>
          {!started
            ? t('game.getReady')
            : remaining === null
              ? t('game.untimed')
              : formatClock(remaining)}
        </div>
        <div className="hud-score" aria-label={t('game.score')}>
          <span className="hud-score-value">{score.basePoints}</span>
          <span className="muted"> {t('game.points')}</span>
        </div>
        <div className="hud-words">
          {target !== null
            ? t('game.wordsOfTarget', { n: state.words.length, target })
            : t('game.wordsCount', { count: state.words.length })}
        </div>
      </header>

      <section className="lang-counters" aria-label={t('game.languageCounters')}>
        {counts.map((c) => {
          const ok = requirement.quotaPerLanguage > 0 && c.words >= requirement.quotaPerLanguage;
          return (
            <span key={c.lang} className={`counter lang-${c.lang}${ok ? ' done' : ''}`}>
              <strong>{c.lang.toUpperCase()}</strong>{' '}
              {requirement.quotaPerLanguage > 0
                ? `${Math.min(c.words, requirement.quotaPerLanguage)}/${requirement.quotaPerLanguage}`
                : c.words}
              {ok ? ' ✓' : ''}
            </span>
          );
        })}
        {requirement.bonusBp > 0 && (
          <span className={`counter bonus${fulfilled ? ' done' : ''}`}>
            {t(`contracts.short.${requirement.contract ?? 'diversity'}`)} +
            {requirement.bonusBp / 100} %
          </span>
        )}
        {opponent && opp && (
          <span
            className={`counter opponent${opponent.online === false ? ' away' : ''}`}
            title={opponent.isBot ? t('game.robotNotice') : undefined}
          >
            {opponent.isBot ? '⚙ ' : ''}
            {opponent.label} — {t('game.wordsCount', { count: opp.words })}
            {opp.finished
              ? ` · ${t('game.opponentDone')}`
              : opponent.online === false
                ? ` · ${t('live.offline')}`
                : ''}
          </span>
        )}
      </section>

      {warnFinal && (
        <div className="banner warn" role="alert">
          {t('game.finalWarning')}
        </div>
      )}

      <section
        className={`current-word${shake ? ' shake' : ''}`}
        aria-label={t('game.currentWord')}
      >
        <div className="current-letters" aria-live="off">
          {key.length > 0 ? (
            key
          ) : (
            <span className="placeholder">{started ? t('game.tapLetters') : ' '}</span>
          )}
        </div>
        {hint && (
          <div className="hint">
            {hint.reason === null
              ? t('game.hintValid', {
                  langs: hint.validLangs.map((l) => l.toUpperCase()).join(' · '),
                })
              : t('game.hintInvalid')}
          </div>
        )}
        {message && (
          <div className={`message ${message.kind}`}>
            {message.text}
            {message.langs &&
              message.langs.map((l) => (
                <button
                  key={l}
                  className="chip small"
                  onClick={() => void doSubmit({ lang: l, wordKey: message.key })}
                >
                  {t('game.attributeTo', { lang: l.toUpperCase() })}
                </button>
              ))}
          </div>
        )}
      </section>

      {langs.length > 1 && (
        <div className="attribution" role="radiogroup" aria-label={t('game.attribution')}>
          <span className="muted small">{t('game.attribution')}</span>
          {(['auto', ...langs] as const).map((l) => (
            <button
              key={l}
              role="radio"
              aria-checked={attrib === l}
              className={`chip${attrib === l ? ' selected' : ''}${l !== 'auto' ? ` lang-${l}` : ''}`}
              onClick={() => setAttrib(l)}
              title={l === 'auto' ? t('game.autoExplain') : undefined}
            >
              {l === 'auto' ? t('game.auto') : l.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      <section className="rack" aria-label={t('game.rack')}>
        {order.map((rackIndex, pos) => {
          const used = picked.includes(rackIndex);
          const letter = started ? rack[rackIndex]! : '?';
          return (
            <button
              key={rackIndex}
              className={`tile${used ? ' used' : ''}${letter.length === 1 && 'ÑÄÖÜ'.includes(letter) ? ' special' : ''}`}
              disabled={!playing || !started || used}
              aria-label={t('game.tileLabel', { letter, pos: pos + 1 })}
              aria-pressed={used}
              onClick={() => pickTile(rackIndex)}
            >
              <span className="tile-letter">{letter}</span>
              <span className="tile-pos" aria-hidden="true">
                {pos + 1}
              </span>
            </button>
          );
        })}
      </section>

      <section className="actions">
        <button
          className="btn ghost"
          onClick={shuffle}
          disabled={!started || !playing}
          aria-label={t('game.shuffle')}
        >
          ⟳ <span className="btn-label">{t('game.shuffle')}</span>
        </button>
        <button
          className="btn ghost"
          onClick={backspace}
          disabled={picked.length === 0}
          aria-label={t('game.delete')}
        >
          ⌫ <span className="btn-label">{t('game.delete')}</span>
        </button>
        <button
          className="btn ghost"
          onClick={clear}
          disabled={picked.length === 0}
          aria-label={t('game.clear')}
        >
          ✕ <span className="btn-label">{t('game.clear')}</span>
        </button>
        <button
          className="btn primary"
          onClick={() => void doSubmit()}
          disabled={key.length < 3 || busy || !playing}
        >
          {t('game.submit')}
        </button>
      </section>

      <section className="found" aria-label={t('game.foundWords')}>
        <ol className="word-list" reversed>
          {[...state.words].reverse().map((w) => (
            <li key={w.seq} className="word-chip">
              <span className={`lang-tag lang-${w.lang}`}>{w.lang.toUpperCase()}</span>
              <span className="word-display">{w.display}</span>
              <span className="word-points">+{w.points}</span>
            </li>
          ))}
        </ol>
      </section>

      <footer className="game-footer">
        <span className="muted small">
          {t(`modes.${mode.id}.name`)} · {langs.map((l) => l.toUpperCase()).join(' + ')}
          {score.language.fulfilled ? ` · ${t('game.bonusSecured')}` : ''}
        </span>
        {playing && (
          <button className="btn ghost small" onClick={() => setConfirmStop(true)}>
            {limit === null ? t('game.finish') : t('game.stop')}
          </button>
        )}
      </footer>

      {state.status === 'finished' && (
        <div className="end-overlay" role="status">
          {t(`results.endReason.${state.endReason ?? 'time_up'}`)} —{' '}
          {formatScore(score.totalCents, locale)}
        </div>
      )}

      {confirm && (
        <Modal
          title={t('game.confirmFinalTitle')}
          onClose={() => setConfirm(null)}
          actions={
            <>
              <button className="btn ghost" onClick={() => setConfirm(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn primary"
                onClick={() => {
                  const c = confirm;
                  setConfirm(null);
                  void doSubmit({ lang: c.lang, confirmFinal: true, wordKey: c.key });
                }}
              >
                {t('game.confirmFinalOk')}
              </button>
            </>
          }
        >
          <p>{t('game.confirmFinalBody')}</p>
          <p className="muted">
            {counts
              .map((c) => `${c.lang.toUpperCase()} ${c.words}/${requirement.quotaPerLanguage}`)
              .join(' · ')}
          </p>
        </Modal>
      )}

      {confirmStop && (
        <Modal
          title={limit === null ? t('game.finish') : t('game.stopTitle')}
          onClose={() => setConfirmStop(false)}
          actions={
            <>
              <button className="btn ghost" onClick={() => setConfirmStop(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn primary"
                onClick={() => {
                  setConfirmStop(false);
                  void driver.stop();
                }}
              >
                {t('common.confirm')}
              </button>
            </>
          }
        >
          <p>{limit === null ? t('game.finishBody') : t('game.stopBody')}</p>
        </Modal>
      )}
    </div>
  );
}
